import { FC, ReactElement } from 'react'

import { Page, View, Text, StyleSheet } from '@react-pdf/renderer'

import { DownloadPdfButtonProps } from 'src/Pages/Calculator/DownloadPdfButton.tsx'
import { numberToMoney, subtractVatFromPrice } from 'src/Helpers/functions.tsx'
import { colors } from 'src/Helpers/constants'
import {
  buildCostDetails,
  buildRelocationRows,
  buildRentFeatures,
  getVatFlags,
} from 'src/Components/Offer/calculation.ts'

const styles = StyleSheet.create({
  horizontalLineBlue: {
    width: '100%',
    height: 1,
    backgroundColor: colors.mainBlue,
  },
  page: {
    padding: 39,
    paddingLeft: 78,
    fontFamily: 'Geometria',
    fontSize: 10,
    color: '#333',
  },
  h1: {
    fontFamily: 'Geometria',
    fontSize: 16,
    fontWeight: 600,
    color: colors.mainBlue,
  },
  h2: {
    fontFamily: 'Geometria',
    fontSize: 14,
    textAlign: 'center',
    textDecoration: 'underline',
    marginBottom: 15,
    marginTop: 20,
  },

  listItem: {
    flexDirection: 'row',
    marginBottom: 5,
  },
  listBullet: {
    width: 10,
    fontFamily: 'Geometria',
  },
  listText: {
    flex: 1,
  },

  table: {
    width: '100%',
    border: '1px solid #bce8f1',
  },
  smallTable: {
    width: '100%',
    borderBottom: '1px solid #bce8f1',
    borderLeft: '1px solid #bce8f1',
    marginBottom: 10,
  },
  tableRow: {
    flexDirection: 'row',
    borderTop: '1px solid #bce8f1',
  },
  tableSubRow: {
    flexDirection: 'row',
    borderTop: '1px solid gray',
  },
  tableColHeader: {
    backgroundColor: '#d9edf7',
    textAlign: 'center',
    padding: 5,
  },
  tableCol: {
    justifyContent: 'center',
    alignItems: 'center',
    padding: 5,
    borderRight: '1px solid #bce8f1',
  },
  tableCell: {
    textAlign: 'center',
    fontSize: 9,
  },

  relocationTableCol_1: { flex: 3 },
  relocationTableCol_2: { flex: 1.5 },
  relocationTableCol_3: { flex: 1.5, borderRight: 0 },

  costTableCol_1: { flex: 3, alignItems: 'flex-start' },
  costTableCol_2: { flex: 1.5 },
  costTableCol_3: { flex: 1.5 },
  costTableCol_4: { flex: 1 },
  costTableCol_5: { flex: 2, borderRight: 0 },
  costTableCol_6: { flex: 7.7, alignItems: 'flex-start' },

  tableSubHeader: {
    backgroundColor: colors.secondGray,
    textAlign: 'center',
    padding: 5,
    width: '100%',
  },
  diamondList: {
    marginTop: 15,
    marginBottom: 15,
  },
  titleWrapper: {
    display: 'flex',
    flexDirection: 'column',
  },
  tableWrapper: {
    display: 'flex',
    flexDirection: 'column',
    gap: 15,
  },
})

interface CalculationPageProps {
  offerEquipments?: DownloadPdfButtonProps['offerEquipments']
  minRentPeriod: DownloadPdfButtonProps['minRentPeriod']
  isMobDemob?: boolean
  vatRate?: number
}

export const CalculationPage: FC<CalculationPageProps> = ({
  offerEquipments = [],
  minRentPeriod,
  isMobDemob,
  vatRate = 22,
}) => {
  const firstEquipment = offerEquipments[0]

  const { isVAT, isNoVat, showBothVatVariants, priceSuffix } = getVatFlags(
    firstEquipment?.vatVisibility,
  )

  const costDetailsData = buildCostDetails({
    offerEquipments,
    vatRate,
    isVAT,
    isNoVat,
  })

  const isAnyEquipmentWithDetalization = offerEquipments.some(
    (item) => item.detalization,
  )

  const relocationRows = buildRelocationRows(offerEquipments)

  const renderRelocationPrice = (cost?: number): ReactElement => {
    if (!cost) return <Text style={styles.tableCell}>-</Text>

    if (showBothVatVariants) {
      return (
        <>
          <Text style={styles.tableCell}>
            {numberToMoney(subtractVatFromPrice(cost, vatRate))} без НДС
          </Text>
          <Text style={styles.tableCell}>{numberToMoney(cost)} с НДС</Text>
        </>
      )
    }

    return (
      <Text style={styles.tableCell}>
        {numberToMoney(isNoVat ? subtractVatFromPrice(cost, vatRate) : cost)}
      </Text>
    )
  }

  const features = buildRentFeatures({
    firstEquipment,
    minRentPeriod,
    isMobDemob,
    hasRelocation: relocationRows.length > 0,
  })

  return (
    <Page size='A4' style={styles.page}>
      <View style={styles.tableWrapper}>
        <View style={styles.titleWrapper}>
          <Text style={styles.h1}>Условия аренды</Text>
          <View style={styles.horizontalLineBlue}></View>
        </View>

        {relocationRows.length > 0 && (
          <View style={styles.table}>
            <View style={[styles.tableRow, { borderTop: 0 }]}>
              <View style={[styles.tableColHeader, { width: '100%' }]}>
                <Text>Перебазировка</Text>
              </View>
            </View>
            <View style={styles.tableRow}>
              <View
                style={[styles.tableColHeader, styles.relocationTableCol_1]}
              >
                <Text>Маршрут</Text>
              </View>
              <View
                style={[styles.tableColHeader, styles.relocationTableCol_2]}
              >
                <Text>Стоимость доставки{priceSuffix} (руб)</Text>
              </View>
              <View
                style={[styles.tableColHeader, styles.relocationTableCol_3]}
              >
                <Text>Стоимость возврата{priceSuffix} (руб)</Text>
              </View>
            </View>

            {relocationRows.map((row, index) => (
              <View key={index} style={styles.tableRow}>
                <View style={[styles.tableCol, styles.relocationTableCol_1]}>
                  <Text style={styles.tableCell}>
                    {row.route ? `${row.name}: ${row.route}` : row.name}
                  </Text>
                </View>
                <View style={[styles.tableCol, styles.relocationTableCol_2]}>
                  {renderRelocationPrice(row.deliveryCost)}
                </View>
                <View style={[styles.tableCol, styles.relocationTableCol_3]}>
                  {renderRelocationPrice(row.returnCost)}
                </View>
              </View>
            ))}
          </View>
        )}

        <View style={styles.smallTable}>
          {features?.map((item, index) => (
            <View key={index} style={styles.tableRow}>
              <View style={[styles.tableCol, styles.relocationTableCol_1]}>
                <Text style={styles.tableCell}>{item.name}</Text>
              </View>
              <View style={[styles.tableCol, styles.relocationTableCol_2]}>
                <Text style={styles.tableCell}>{item.value}</Text>
              </View>
            </View>
          ))}
        </View>
      </View>

      {isAnyEquipmentWithDetalization && (
        <View style={styles.tableWrapper}>
          <View style={styles.titleWrapper}>
            <Text style={styles.h1}>Детализация стоимости</Text>
            <View style={styles.horizontalLineBlue}></View>
          </View>

          <View style={styles.table}>
            <View style={[styles.tableRow, { borderTop: 0 }]}>
              <View style={[styles.tableColHeader, styles.costTableCol_1]}>
                <Text>Наименование</Text>
              </View>
              <View style={[styles.tableColHeader, styles.costTableCol_2]}>
                <Text>Цена, руб</Text>
              </View>
              <View style={[styles.tableColHeader, styles.costTableCol_3]}>
                <Text>Количество в месяц</Text>
              </View>
              <View style={[styles.tableColHeader, styles.costTableCol_4]}>
                <Text>Ед. изм.</Text>
              </View>
              <View style={[styles.tableColHeader, styles.costTableCol_5]}>
                <Text>Итого, руб</Text>
              </View>
            </View>

            {costDetailsData.map((item, index) => {
              return (
                <View key={index}>
                  <View>
                    {item.detalization &&
                      typeof item.detalization === 'object' &&
                      item.detalization.map((detalization, index) => {
                        return (
                          <View key={index}>
                            <View key={index} style={styles.tableRow}>
                              <View
                                style={[styles.tableSubHeader, { padding: 0 }]}
                              >
                                <Text>
                                  {detalization.detalizationSubheader}
                                </Text>
                              </View>
                            </View>
                            {detalization.detalizationRows.map((row, index) => {
                              return (
                                <View key={index} style={styles.tableRow}>
                                  <View
                                    style={[
                                      styles.tableCol,
                                      styles.costTableCol_1,
                                    ]}
                                  >
                                    <Text
                                      style={[
                                        styles.tableCell,
                                        { textAlign: 'left' },
                                      ]}
                                    >
                                      {row.name}
                                    </Text>
                                  </View>
                                  <View
                                    style={[
                                      styles.tableCol,
                                      styles.costTableCol_2,
                                    ]}
                                  >
                                    <Text style={styles.tableCell}>
                                      {row.price}
                                    </Text>
                                  </View>
                                  <View
                                    style={[
                                      styles.tableCol,
                                      styles.costTableCol_3,
                                    ]}
                                  >
                                    <Text style={styles.tableCell}>
                                      {row.quantity}
                                    </Text>
                                  </View>
                                  <View
                                    style={[
                                      styles.tableCol,
                                      styles.costTableCol_4,
                                    ]}
                                  >
                                    <Text style={styles.tableCell}>
                                      {row.units}
                                    </Text>
                                  </View>
                                  <View
                                    style={[
                                      styles.tableCol,
                                      styles.costTableCol_5,
                                    ]}
                                  >
                                    <Text style={styles.tableCell}>
                                      {row.total}
                                    </Text>
                                  </View>
                                </View>
                              )
                            })}
                          </View>
                        )
                      })}
                  </View>

                  {item.detalization &&
                    typeof item.detalization === 'object' && (
                      <View>
                        {!isVAT && (
                          <View key={index} style={styles.tableRow}>
                            <View
                              style={[styles.tableCol, styles.costTableCol_6]}
                            >
                              <Text
                                style={[
                                  styles.tableCell,
                                  { textAlign: 'left' },
                                ]}
                              >
                                Стоимость аренды техники без НДС, руб/ч
                              </Text>
                            </View>

                            <View
                              style={[styles.tableCol, styles.costTableCol_5]}
                            >
                              <Text style={styles.tableCell}>
                                {numberToMoney(
                                  Number(item.totalPriceWithoutVat),
                                )}
                              </Text>
                            </View>
                          </View>
                        )}
                        {!isNoVat && (
                          <View key={index} style={styles.tableRow}>
                            <View
                              style={[styles.tableCol, styles.costTableCol_6]}
                            >
                              <Text
                                style={[
                                  styles.tableCell,
                                  { textAlign: 'left' },
                                ]}
                              >
                                Стоимость аренды техники с НДС, руб/ч
                              </Text>
                            </View>

                            <View
                              style={[styles.tableCol, styles.costTableCol_5]}
                            >
                              <Text style={styles.tableCell}>
                                {numberToMoney(Number(item.totalPriceWithVat))}
                              </Text>
                            </View>
                          </View>
                        )}
                      </View>
                    )}
                </View>
              )
            })}
          </View>
        </View>
      )}
    </Page>
  )
}
